import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PassageImage from '../../components/PassageImage/PassageImage';

interface SingleSnapshotViewProps {
  approaches: any[];
  snapTs: number;
  selectedIndex: number;
  setExpandedIdx: (idx: number) => void;
  onClose?: () => void;
  size?: 'sidebar' | 'full';
}

const SingleSnapshotView: React.FC<SingleSnapshotViewProps> = ({
  approaches,
  snapTs,
  selectedIndex,
  onClose,
  setExpandedIdx,
  size = 'full',
}) => {
  const sliderRef = useRef<Slider>(null);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!sliderRef.current) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          sliderRef.current.slickPrev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          sliderRef.current.slickNext();
          break;
        case 'Escape':
          event.preventDefault();
          if (onClose) onClose();
          break;
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: selectedIndex,
    arrows: true,
    adaptiveHeight: true,
    afterChange: (current: number) => {
      setExpandedIdx(current);
    },
  };

  const minHeight = size === 'sidebar' ? '150px' : '100%';

  return (
    <Box sx={{ minHeight: minHeight, width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          '& .slick-slider': {
            width: '100%',
            minHeight: minHeight,
          },
          '& .slick-track': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .slick-slide': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
          },
          '& .slick-dots': {
            bottom: 2,
          },
          '& .slick-prev': {
            left: 0,
          },
          '& .slick-next': {
            right: 0,
          },
          minHeight: '100%',
          padding: 1,
        }}
      >
        <Slider ref={sliderRef} {...settings}>
          {approaches.map((approach: any) => (
            <Box
              key={approach.direction}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                height: '100%',
              }}
            >
              <PassageImage
                filename={approach.filename}
                description={approach.description}
                imageUrl={import.meta.env.VITE_REACT_APP_CAMERA_URL}
                expanded
                onClose={onClose}
                clickable={size === 'sidebar' ? false : true}
              />
            </Box>
          ))}
        </Slider>
      </Box>
    </Box>
  );
};

export default SingleSnapshotView;
